import { useState, useEffect, useRef } from "react";
import { Box, LinearProgress, Typography } from "@/components/ui";

const DEFAULT_DURATION_MS = 700;
const PROGRESS_TRANSITION_MS = 600;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Renders "count / limit" with an optional progress bar.
 * Count animates from 0 to value; the bar fill is synced with the same count.
 *
 * @param {number} value - Current count (target to animate to)
 * @param {number} limit - Total/capacity (denominator)
 * @param {number} duration - Animation duration in ms
 * @param {boolean} showProgressBar - Whether to show a progress bar below
 * @param {string} progressColor - MUI color for the bar (primary, success, etc.)
 * @param {function} getProgressColor - Optional (percent) => color for bar
 * @param {object} numberProps - Props for the main number Typography
 * @param {object} limitProps - Props for the " / limit" part
 */
export function AnimatedCountWithLimit({
  value = 0,
  limit = 100,
  duration = DEFAULT_DURATION_MS,
  showProgressBar = false,
  progressColor = "primary",
  getProgressColor,
  numberProps = {},
  limitProps = {},
  isRTL = false,
  ...boxProps
}) {
  const [displayCount, setDisplayCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Math.max(0, Number(value) || 0);
    setDisplayCount(0);
    const start = performance.now();

    const run = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      setDisplayCount(Math.round(target * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(run);
      }
    };
    rafRef.current = requestAnimationFrame(run);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [value, duration]);

  const limitNum = Math.max(1, Number(limit) || 1);
  const percent = Math.min(100, (displayCount / limitNum) * 100);
  const barColor =
    typeof getProgressColor === "function"
      ? getProgressColor(percent)
      : progressColor;

  return (
    <Box {...boxProps}>
      {isRTL ? (
        <>
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            {...limitProps}
          >
            {limitNum}
            {" "}
            /
          </Typography>
          <Typography component="span" {...numberProps}>
            {" "}
            {displayCount}
          </Typography>
        </>
      ) : (
        <>
          <Typography component="span" {...numberProps}>
            {displayCount}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            {...limitProps}
          >
            {" "}
            / {limitNum}
          </Typography>
        </>
      )}
      {showProgressBar && (
        <LinearProgress
          variant="determinate"
          value={percent}
          color={barColor}
          sx={{
            height: 8,
            borderRadius: 1,
            mt: 1,
            "& .MuiLinearProgress-bar": {
              transition: `transform ${PROGRESS_TRANSITION_MS}ms ease-out`,
            },
          }}
        />
      )}
    </Box>
  );
}

export default AnimatedCountWithLimit;
